import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchStaffTeam, inviteStaffMember, resendStaffSetupLink, type StaffMember } from '../../utils/staffTeam';
import type { StaffRole } from '../../utils/staffAuth';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';

/**
 * Portal Productions staff accounts. Owners only — the list and the invitation
 * both come from the staff-team Edge Function, which re-checks the role
 * server-side, so hiding the page is presentation rather than protection.
 */
export function Team() {
  const {
    staffUser,
    notify,
    goPortal
  } = usePortal();
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState('');
  async function load() {
    setLoading(true);
    const result = await fetchStaffTeam();
    if (!result.ok) setLoadError(result.error ?? 'The team could not be loaded.');else {
      setLoadError('');
      setMembers(result.members ?? []);
    }
    setLoading(false);
  }
  useEffect(() => {
    void load();
  }, []);
  async function resend(address: string) {
    if (resending) return;
    setResending(address);
    const result = await resendStaffSetupLink(address);
    setResending('');
    notify(result.ok ? `New setup link sent to ${address}` : 'Setup link could not be sent.');
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    const address = email.trim().toLowerCase();
    const result = await inviteStaffMember(address, role);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Invitation could not be sent.');
      return;
    }
    notify(`Invitation sent to ${address}`);
    setEmail('');
    setRole('staff');
    setOpen(false);
    void load();
  }
  if (staffUser?.role !== 'owner') {
    return <section aria-labelledby="team" data-id="element-790" data-mp="2sa4shefm2ypz">
        <h1 id="team" className="font-display text-4xl leading-none tracking-tight sm:text-5xl" data-id="element-791" data-mp="2sb669qo0hzbe">
          Team
        </h1>
        <p className="mt-8 font-display text-xl text-paper/40" data-id="element-792" data-mp="3pv0taoigzjb8">
          Only Portal owners can manage staff accounts.
        </p>
      </section>;
  }
  return <section aria-labelledby="team" data-id="element-793" data-mp="3fxw7ipsjdpuy">
      <button type="button" onClick={() => goPortal('projects')} className="label text-paper/35 transition-colors duration-150 ease-out hover:text-paper" data-id="element-794" data-mp="3g10hk3sonftj">
        ← Client Projects
      </button>

      <h1 id="team" className="mt-10 font-display text-4xl leading-none tracking-tight sm:text-5xl" data-id="element-795" data-mp="1zkfpivjccolq">
        Team
      </h1>
      <p className="label-sm mt-6 text-paper/40" data-id="element-796" data-mp="f4pyv1c98254">Portal Productions staff accounts.</p>

      <ul className="m-0 mt-14 list-none p-0" data-id="element-797" data-mp="k2j3vr1tmq3t">
        {loading && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-798" data-mp="p0syo0jmvoow">
            Loading team…
          </li>}

        {!loading && loadError && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-799" data-mp="yr5s6i9fes76">
            {loadError}
          </li>}

        {!loading && !loadError && members.map(member => <li key={member.userId} className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3 border-b border-ruleDark py-7" data-id="element-800" data-mp="17slvk61tpu1w">
              <div className="min-w-0" data-id="element-801" data-mp="39jqkgucdggij">
                <p className="m-0 font-display text-2xl leading-none" data-id="element-802" data-mp="39krx3391db2d">{member.name}</p>
                <p className="label-sm mt-3 text-paper/30" data-id="element-803" data-mp="3eijzir7b4fkt">{member.email}</p>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2" data-id="element-804" data-mp="18sg8vvnumav7">
                {member.pending && <>
                    <span className="label-sm text-paper/30" data-id="element-805" data-mp="13x121no9df8a">Invited</span>
                    {/* Presentation only — the function re-checks the role. */}
                    <button type="button" onClick={() => resend(member.email)} disabled={resending === member.email} className="label-sm text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper disabled:text-paper/20 disabled:hover:text-paper/20" data-id="element-806" data-mp="1du5nflaa56o1">
                      Resend Setup Link
                    </button>
                  </>}
                <span className="label text-paper/50" data-id="element-807" data-mp="6dpsnucf3xgo">{member.role}</span>
              </div>
            </li>)}
      </ul>

      {open ? <form onSubmit={submit} className="mt-12 max-w-xl" data-id="element-808" data-mp="3ofenz9d5jjf1">
          <p className="label m-0 text-paper/40" data-id="element-809" data-mp="3odtttkur22hp">Invite Team Member</p>

          <label className="mt-9 block" data-id="element-810" data-mp="3t6wltojvp3vs">
            <span className="label block text-paper/40" data-id="element-811" data-mp="3t7o8uazettag">Email</span>
            <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="e.g. name@portalproductions.me" autoFocus className={FIELD} data-id="element-812" data-mp="3eia117bhfjx6" />
          </label>

          <label className="mt-9 block" data-id="element-813" data-mp="fya3oupdl06v">
            <span className="label block text-paper/40" data-id="element-814" data-mp="fx8nkbti5nia">Role</span>
            <select value={role} onChange={event => setRole(event.target.value as StaffRole)} className={SELECT} data-id="element-815" data-mp="1199mkjpot58">
              <option value="staff" className={OPTION} data-id="element-816" data-mp="2ls0b7zasmpvh">
                Staff
              </option>
              <option value="owner" className={OPTION} data-id="element-817" data-mp="2qrmno6iss238">
                Owner
              </option>
            </select>
          </label>

          {error && <p role="alert" className="label-sm mt-8 text-paper" data-id="element-818" data-mp="xagmm4kwbdie">
              {error}
            </p>}

          <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-819" data-mp="niko4sewdldn">
            <button type="submit" disabled={busy} className={BUTTON_SOLID} data-id="element-820" data-mp="ngrgb9sjsv42">
              Send Invitation
            </button>
            <button type="button" onClick={() => setOpen(false)} className={BUTTON_QUIET} data-id="element-821" data-mp="dkeo6gcnipbf">
              Cancel
            </button>
          </div>
        </form> : <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-12 inline-flex items-center gap-3`} data-id="element-822" data-mp="9l5vletckxhg">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-823" data-mp="3vrkuzid4uiwe" />
          Invite Team Member
        </button>}
    </section>;
}